import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { firestore } from '../firebase'
import { useAuth } from '../auth/AuthProvider'

/**
 * Full PWA-facing UserSettings shape. Mirrors the desktop's AppSettings
 * (src/renderer/store/settingsStore.ts) for every setting that makes sense
 * in a browser context. Desktop-only fields (file scanning, cleanup, torrent
 * config, native player, OpenSubtitles, etc.) are intentionally omitted.
 *
 * Every field is optional — the PWA always falls back to a safe default
 * (see DEFAULT_USER_SETTINGS below) if the Firestore document is missing a
 * key. This lets the PWA work for users who only ever use the phone and
 * never touched the desktop app.
 */
export interface UserSettings {
  // Appearance
  theme?: 'dark' | 'light' | null
  animationSpeed?: 'slow' | 'normal' | 'fast' | 'none' | null
  showMovieRatings?: boolean | null
  showMovieDuration?: boolean | null

  // Library defaults
  defaultSortOrder?:
    | 'title'
    | 'year'
    | 'dateAdded'
    | 'rating'
    | 'duration'
    | 'userRating'
    | null
  defaultViewMode?: 'grid' | 'carousel' | null
  showVirtualMoviesInLibrary?: boolean | null
  autoMarkAsSeenOnPlay?: boolean | null

  // TMDB
  tmdbApiKey?: string | null
  tmdbPreferredLanguage?: string | null
  tmdbIncludeAdultContent?: boolean | null
  tmdbAutoImportEnabled?: boolean | null
  tmdbSearchConfidenceThreshold?: number | null
  tmdbAutoMatchThreshold?: number | null

  // Groq AI
  groqApiKey?: string | null
  aiSearchEnabled?: boolean | null

  // Recommendations
  recommendationSystemEnabled?: boolean | null
  enablePersonalizedRecommendations?: boolean | null
  trackUserInteractions?: boolean | null

  // Onboarding
  hasCompletedOnboarding?: boolean | null

  // Metadata
  updatedAt?: string | null
}

/**
 * Safe defaults used whenever a Firestore value is missing. These match the
 * desktop defaults in src/renderer/store/settingsStore.ts where applicable.
 */
export const DEFAULT_USER_SETTINGS: Required<
  Omit<UserSettings, 'tmdbApiKey' | 'groqApiKey' | 'updatedAt'>
> & {
  tmdbApiKey: string | null
  groqApiKey: string | null
} = {
  theme: 'dark',
  animationSpeed: 'normal',
  showMovieRatings: true,
  showMovieDuration: true,

  defaultSortOrder: 'dateAdded',
  defaultViewMode: 'grid',
  showVirtualMoviesInLibrary: false,
  autoMarkAsSeenOnPlay: true,

  tmdbApiKey: null,
  tmdbPreferredLanguage: 'en',
  tmdbIncludeAdultContent: false,
  tmdbAutoImportEnabled: false,
  tmdbSearchConfidenceThreshold: 0.8,
  tmdbAutoMatchThreshold: 0.9,

  groqApiKey: null,
  aiSearchEnabled: true,

  recommendationSystemEnabled: true,
  enablePersonalizedRecommendations: true,
  trackUserInteractions: true,

  hasCompletedOnboarding: false,
}

type DefaultKey = keyof typeof DEFAULT_USER_SETTINGS
type DefaultValue<K extends DefaultKey> = NonNullable<
  (typeof DEFAULT_USER_SETTINGS)[K]
>

/**
 * Read a setting with a guaranteed-present fallback. Use this instead of
 * `settings?.foo ?? defaultValue` in consumers so defaults stay in one
 * place. The return type strips null so panel components can feed the
 * value straight into controls that expect a concrete primitive.
 */
export function getSetting<K extends DefaultKey>(
  settings: UserSettings | null | undefined,
  key: K
): DefaultValue<K> {
  const v = settings?.[key as keyof UserSettings]
  if (v === undefined || v === null) {
    return DEFAULT_USER_SETTINGS[key] as DefaultValue<K>
  }
  return v as DefaultValue<K>
}

/**
 * Bundled TMDB API key baked into the PWA build. When set via
 * `VITE_TMDB_BUNDLED_API_KEY` at build time, every TMDB call falls back
 * to this key so users don't need to register their own TMDB account.
 * A user-supplied key in their Firestore settings always takes priority;
 * this is the floor, not the ceiling.
 *
 * Lives in the bundle, not in `DEFAULT_USER_SETTINGS`, so we never write
 * it back to Firestore (we want to remember "user hasn't set one" so
 * they can connect their own account later for personalized features).
 */
const BUNDLED_TMDB_API_KEY = (import.meta.env?.VITE_TMDB_BUNDLED_API_KEY as string | undefined) || ''

/**
 * Resolves the TMDB API key to actually use for outbound calls. Returns
 * the user's personal key if they've set one, otherwise the bundled
 * developer key, otherwise null when the build ships without a bundled
 * key and the user hasn't set one.
 */
export function getEffectiveTmdbApiKey(
  settings: UserSettings | null | undefined
): string | null {
  const userKey = (settings?.tmdbApiKey ?? '').trim()
  if (userKey) return userKey
  if (BUNDLED_TMDB_API_KEY) return BUNDLED_TMDB_API_KEY
  return null
}

/**
 * Whether the live TMDB connection is using the bundled developer key
 * (i.e. the user hasn't supplied their own and one was baked into the
 * build). Used by settings UI to surface "already connected — supplying
 * your own key is optional" framing.
 */
export function isUsingBundledTmdbApiKey(
  settings: UserSettings | null | undefined
): boolean {
  const userKey = (settings?.tmdbApiKey ?? '').trim()
  if (userKey) return false
  return Boolean(BUNDLED_TMDB_API_KEY)
}

/**
 * Subscribes to users/{uid}/settings/frontend so the PWA can read the
 * TMDB/Groq API keys and every other shared setting the desktop app wrote.
 * Avoids hardcoding credentials in the bundle and avoids a separate
 * per-PWA setup flow — the phone can edit the same doc the desktop uses.
 */
export function useUserSettings(): {
  settings: UserSettings | null
  loading: boolean
} {
  const { user } = useAuth()
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setSettings(null)
      setLoading(false)
      return
    }
    const ref = doc(firestore, `users/${user.uid}/settings/frontend`)
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setSettings(snap.exists() ? (snap.data() as UserSettings) : {})
        setLoading(false)
      },
      (err) => {
        console.error('[useUserSettings]', err)
        setLoading(false)
      }
    )
    return unsub
  }, [user])

  return { settings, loading }
}
